export type Periodicidade = 'MENSAL' | 'TRIMESTRAL' | 'ANUAL';

/**
 * Os tetos do plano. `null` = ilimitado, NUNCA 0 — zero significa "nenhum", e
 * a diferença entre "sem teto" e "não pode nada" é o produto inteiro.
 *
 * Espelham as colunas `limite_*` do catálogo, e cada um é aplicado por um
 * serviço: sociedades, usuários e atos do mês.
 */
export type LimitesDoPlano = {
  sociedades: number | null;
  usuarios: number | null;
  atosPorMes: number | null;
};

export type Plano = {
  codigo: string;
  nome: string;
  periodicidade: Periodicidade;
  /** O que é cobrado POR CICLO — R$ 838,80 de uma vez no anual, nunca 6990. */
  precoCentavos: number;
  limites: LimitesDoPlano;
};

const MESES_POR_CICLO: Record<Periodicidade, number> = {
  MENSAL: 1,
  TRIMESTRAL: 3,
  ANUAL: 12,
};

export function formatarCentavos(centavos: number): string {
  // `toLocaleString` insere um espaço NBSP (U+00A0) entre "R$" e o número.
  // Normalizado para espaço comum: visualmente idêntico, mas previsível em
  // teste/comparação de string e em qualquer lugar que copie o texto.
  return (centavos / 100)
    .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    .replace(' ', ' ');
}

/**
 * O "a partir de R$ 69,90/mês" do anual é comunicação de vitrine — a
 * cobrança real é `precoCentavos` de uma vez. Por isso o equivalente mensal
 * é sempre DERIVADO por divisão aqui, nunca lido de uma coluna separada:
 * guardar 6990 à parte e mostrar os dois números soltos é como a fatura
 * derrapa e cobra R$ 69,90 de quem contratou R$ 838,80 (ver
 * `docs/superpowers/specs/2026-08-26-assinaturas-e-planos-design.md`, §5).
 */
export function equivalenteMensalCentavos(
  plano: Pick<Plano, 'periodicidade' | 'precoCentavos'>,
): number {
  return Math.round(plano.precoCentavos / MESES_POR_CICLO[plano.periodicidade]);
}

export type PlanoFormatado = {
  /** Ex.: "R$ 69,90/mês" — só para comparação visual entre os três cards. */
  precoMensalEquivalente: string;
  /**
   * Ex.: "R$ 838,80 cobrados de uma vez" no anual, "R$ 99,90/mês" no mensal.
   * Esta é a frase que decide se a página engana ou não sobre a cobrança —
   * nunca omitir "cobrados de uma vez" num ciclo maior que um mês.
   */
  precoCobrado: string;
  cicloEmMeses: number;
};

export function formatarPlano(plano: Plano): PlanoFormatado {
  const equivalente = equivalenteMensalCentavos(plano);
  const cicloEmMeses = MESES_POR_CICLO[plano.periodicidade];

  return {
    precoMensalEquivalente: `${formatarCentavos(equivalente)}/mês`,
    precoCobrado:
      plano.periodicidade === 'MENSAL'
        ? `${formatarCentavos(plano.precoCentavos)}/mês`
        : `${formatarCentavos(plano.precoCentavos)} cobrados de uma vez`,
    cicloEmMeses,
  };
}
